/**
 * React hook for checking multiple feature flags.
 */

import { useEffect, useState, useMemo } from 'react';
import { PostHogManager } from '#~/posthog/manager.js';
import type { FeatureFlagOptions } from '#~/posthog/types.js';

/**
 * Hook to get multiple feature flag values.
 *
 * @param flags - Array of feature flag names
 * @param options - Options for flag retrieval
 * @returns Object with flags map (undefined entries while loading), loading state, and error
 *
 * @example
 * ```typescript
 * const { flags, loading, error } = useFeatureFlags([
 *   'AUTOTRACKING',
 *   'STUDIO_UI_DEBUGGING'
 * ], {
 *   timeout: 5000
 * });
 *
 * if (loading) return <Spinner />;
 * if (flags.AUTOTRACKING) return <AutoTracking />;
 * return null;
 * ```
 */
export function useFeatureFlags(
  flags: string[],
  options?: {
    timeout?: number;
    enableRealtime?: boolean; // New option
  }
) {
  const [flagsMap, setFlagsMap] = useState<Record<string, boolean | undefined>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number>(Date.now()); // New state

  // Stabilize flags reference to avoid unnecessary re-renders
  const stableFlags = useMemo(() => flags, [flags.join(',')]);

  useEffect(() => {
    let mounted = true;
    let unsubscribeCallbacks: (() => void)[] = [];

    const manager = PostHogManager.getInstance();

    setLoading(true);
    setError(null);
    setFlagsMap(() => {
      const placeholder: Record<string, boolean | undefined> = {};
      stableFlags.forEach((flag) => {
        placeholder[flag] = undefined;
      });
      return placeholder;
    });

    const loadFlags = async () => {
      try {
        const flagOptions: FeatureFlagOptions = {};
        if (options?.timeout !== undefined) flagOptions.timeout = options.timeout;
        if (options?.enableRealtime !== undefined)
          flagOptions.enableRealtime = options.enableRealtime;

        const results = await Promise.all(
          stableFlags.map((flag) => manager.getFeatureFlag(flag, flagOptions))
        );

        if (mounted) {
          const map: Record<string, boolean> = {};
          stableFlags.forEach((flag, index) => {
            map[flag] = Boolean(results[index]);
          });
          setFlagsMap(map);
          setLoading(false);
          setLastUpdated(Date.now());
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      }
    };

    // Set up real-time subscriptions (unless disabled)
    if (options?.enableRealtime !== false) {
      stableFlags.forEach((flag) => {
        const unsubscribe = manager.subscribeToFlag(flag, (value) => {
          if (mounted) {
            setFlagsMap((prev) => ({ ...prev, [flag]: value }));
            setLastUpdated(Date.now());
            // Clear any previous errors since we got an update
            setError(null);
          }
        });
        unsubscribeCallbacks.push(unsubscribe);
      });
    }

    loadFlags();

    return () => {
      mounted = false;
      unsubscribeCallbacks.forEach((unsubscribe) => unsubscribe());
    };
  }, [stableFlags, options?.timeout, options?.enableRealtime]);

  return {
    /**
     * Map of feature flag names to their enabled status once loading completes.
     * Entries are undefined while individual flags are loading.
     */
    flags: flagsMap,

    /**
     * Whether the flags are still loading.
     */
    loading,

    /**
     * Error if flag retrieval failed.
     */
    error,

    /**
     * Timestamp of the last update.
     */
    lastUpdated
  };
}
